import { Framework } from '@/framework';
import { IPoint } from '@/version-types';
import { getContextMenuState } from './states/context-menu-state';
import { ContextMenuEvents, GetCustomContextMenuFn, IExportContextMenuItem, IRenderContextMenuItemArgs } from './type';

export class ContextMenuPlugin extends Framework {
    /** 右键菜单位置信息 */
    positionInfo = {
        x: 0,
        y: 0,
        bottom: 0,
        right: 0,
        arrowMenuInRight: true,
    };
    /** 右键菜单激活信息 */
    activeInfo: IRenderContextMenuItemArgs = {
        activeLayer: null,
        pickLayer: null,
        isImageEditor: false,
        operateLayerType: '',
    };

    /**
     * 全量替换当前右键菜单项（通常传入 getBuiltinContextMenuItems() 再拼接自定义项，或完全自定义列表）
     */
    setContextMenuItems(items: Array<IExportContextMenuItem>) {
        getContextMenuState(this.identity).setContextMenuList(items);
        return this;
    }

    /** 在已有菜单项后追加（内置项由状态初始化加载，也可用 setContextMenuItems 覆盖） */
    addContextMenu(contextMenuList: Array<IExportContextMenuItem>) {
        getContextMenuState(this.identity).addContextMenu(contextMenuList);
        return this;
    }

    /** 设置箭头显示左边的间距 */
    setArrowMenuInLeftSpace(arrowMenuInLeftSpace: number) {
        getContextMenuState(this.identity).setArrowMenuInLeftSpace(arrowMenuInLeftSpace);
        return this;
    }

    /** 隐藏内置菜单项 */
    setHiddenContextMenu(hiddenContextMenuTypes: Array<string>) {
        getContextMenuState(this.identity).setHiddenContextMenuTypes(hiddenContextMenuTypes);
        return this;
    }

    /** 注册获取自定义右键菜单配置的方法 */
    registerContextMenu(getCustomContextMenuFn: GetCustomContextMenuFn) {
        getContextMenuState(this.identity).setCustomContextMenuFn(getCustomContextMenuFn);
    }

    /** 注销获取自定义右键菜单配置的方法 */
    unregisterContextMenu() {
        getContextMenuState(this.identity).setCustomContextMenuFn(() => null);
        return this;
    }

    /** 禁用右键菜单 */
    disableMenu() {
        getContextMenuState(this.identity).setDisableMenu(true);
        return this;
    }

    /** 启用右键菜单 */
    enableMenu() {
        getContextMenuState(this.identity).setDisableMenu(false);
        return this;
    }

    /** 监听右键菜单状态事件 */
    onState(cb: (isShow: boolean) => void) {
        const handler = this.framework.service.events.on(ContextMenuEvents.stateChange, cb);
        return () => this.framework.service.events.off(handler);
    }

    /** 设置右键菜单状态 */
    setState(isShow: boolean) {
        this.framework.service.events.emit(ContextMenuEvents.stateChange, isShow);
        return this;
    }

    /** 监听打开我的空间模态框事件 */
    onAddImage(callback: () => void, weight = 0) {
        const handler = this.framework.service.events.on(ContextMenuEvents.addImage, callback, weight);
        return () => this.framework.service.events.off(handler);
    }

    /** 添加图片 */
    addImage() {
        this.framework.service.events.emit(ContextMenuEvents.addImage);
        return this;
    }

    /** 监听保存到我的空间事件 */
    onSaveToMySpace(callback: () => void, weight = 0) {
        const handler = this.framework.service.events.on(ContextMenuEvents.saveToMySpace, callback, weight);
        return () => this.framework.service.events.off(handler);
    }

    /** 添加到我的空间 */
    saveToMySpace() {
        this.framework.service.events.emit(ContextMenuEvents.saveToMySpace);
        return this;
    }

    /** 关闭右键菜单 */
    close() {
        this.framework.service.events.emit(ContextMenuEvents.close);
        return this;
    }

    /** 监听右键菜单关闭事件 */
    onClose(cb: () => void) {
        const handler = this.framework.service.events.on(ContextMenuEvents.close, cb);
        return () => this.framework.service.events.off(handler);
    }

    /** 监听右键菜单打开事件 */
    onShow(cb: (point: IPoint) => void) {
        const handler = this.framework.service.events.on(ContextMenuEvents.show, cb);
        return () => this.framework.service.events.off(handler);
    }

    /** 展示右键菜单 */
    show(point: IPoint) {
        this.framework.service.events.emit(ContextMenuEvents.show, point);
        return this;
    }
}
